import Storage from '../storage/Storage';

class HttpClient {
    /**
     * Converts response.json() to <T>, only if status code is OK.
     * Otherwise, will throw an error.
     * @param response Response
     */
    static async getData<T>(response: Response): Promise<T> {
        if (response.status < 200 || response.status > 299) {
            throw new Error('Received status code is not OK.')
        }
        return await response.json() satisfies T;
    }

    static async getNoContent(response: Response): Promise<void> {
        if (response.status < 200 || response.status > 299) {
            throw new Error('Received status code is not OK.')
        }
    }

    /**
     * Construct list of headers.
     * Will skip authorization header, if token in session storage is not found.
     * @author Kacper Faber
     * @since 0.1.5
     */
    static getFormDataHeaders(): any {
        const token = Storage.getToken();

        if (token === null) {
            return new Headers({});
        }

        return new Headers({
            'Authorization': `${token}`
        });
    }

    /**
     * Construct list of headers.
     * Will skip authorization header, if token in session storage is not found.
     * @author Kacper Faber
     */
    static getHeaders(): any {
        const token = Storage.getToken();

        if (token === null) {
            return new Headers({
                'Content-Type': 'application/json'
            });
        }

        return new Headers({
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
        });
    }

    /**
     * Get query object, skip undefined fields.
     * @author Kacper Faber
     * @param params
     */
    static getParams(params: any) {
        const obj = { ...params };
        Object.keys(obj).forEach(key => {
            if (obj[key] === undefined) {
                delete obj[key];
            }
        });
        return obj;
    }

    static getUrl(url: string, params: any): string {
        let filteredParams = this.getParams(params);
        const searchParams = new URLSearchParams(filteredParams);
        return `${url}?${searchParams}`;
    }

    // noinspection JSUnusedGlobalSymbols
    static async get<T>(url: string, params = {}): Promise<T> {
        const urlWithParams = HttpClient.getUrl(url, params);
        const headers = HttpClient.getHeaders();
        const init: any = {method:'GET', headers, mode: 'cors'};
        const response = await fetch(urlWithParams, init);
        if (response.status < 200 || response.status > 299) {
            throw new Error('Invalid');
        }
        return await response.json() satisfies T
    }

    // noinspection JSUnusedGlobalSymbols
    static async put<T>(url: string, params: any = {}, body: any): Promise<T> {
        const urlWithParams = HttpClient.getUrl(url, params);
        const headers = HttpClient.getHeaders();
        const bodyJson = JSON.stringify(body);
        const init: any = { method: 'PUT', headers, body: bodyJson, mode: 'cors' };
        const response = await fetch(urlWithParams, init);
        return this.getData(response);
    }

    // noinspection JSUnusedGlobalSymbols
    static async post<T>(url: string, params: any = {}, body: any): Promise<T> {
        const urlWithParams = HttpClient.getUrl(url, params);
        const headers = HttpClient.getHeaders();
        const bodyJson = JSON.stringify(body);
        const init: any = { method: 'POST', headers, body: bodyJson, mode: 'cors' };
        const response = await fetch(urlWithParams, init);
        return this.getData(response);
    }

    // noinspection JSUnusedGlobalSymbols
    static async execNoContent(url: string, method: string, params: any = {}, body: any): Promise<void> {
        const bodyJson = JSON.stringify(body);
        const init: any = { method, headers: HttpClient.getHeaders(), body: bodyJson, mode: 'cors' };
        const response = await fetch(HttpClient.getUrl(url, params), init);
        return this.getNoContent(response);
    }

    /**
     * Converts object to FormData
     * @param obj
     * @returns FormData
     * @author Kacper Faber
     * @since 0.1.5
     */
    static toFormData(obj: any): FormData {
        const formData = new FormData();

        for (const key in obj) {
            if (obj[key] === undefined) {
                continue;
            }

            formData.append(key, obj[key]);
        }

        return formData;
    }

    /**
     * Sends POST request with form data.
     * @param url
     * @param params
     * @param body
     * @author Kacper Faber
     * @since 0.1.5
     */
    static async postFormData<T>(url: string, params: any = {}, body: any): Promise<T> {
        const init: any = {
            method: 'POST',
            headers: this.getFormDataHeaders(),
            body: this.toFormData(body),
            mode: 'cors'
        };
        const response = await fetch(HttpClient.getUrl(url, params), init);
        return this.getData(response);
    }

    /**
     * Sends PUT request with form data.
     * @param url
     * @param params
     * @param body
     * @author Kacper Faber
     * @since 0.1.5
     */
    static async putFormData<T>(url: string, params: any = {}, body: any): Promise<T> {
        const init: any = {
            method: 'PUT',
            headers: this.getFormDataHeaders(),
            body: this.toFormData(body),
            mode: 'cors'
        };
        const response = await fetch(HttpClient.getUrl(url, params), init);
        return this.getData(response);
    }

    static async getResponse(url: string, params: any = {}): Promise<Response> {
        const init: any = {
            method: 'GET',
            headers: this.getHeaders(),
            mode: 'cors'
        }
        const response = await fetch(HttpClient.getUrl(url, params), init);
        if (response.status >= 200 && response.status <= 299) {
            return response;
        }
        throw new Error('Not positive status code');
    }
}

// noinspection JSUnusedGlobalSymbols
export default HttpClient;

// TODO: HttpClient.ts
//  - Optimize code, these functions are duplicates but with different methods.